'use client'
import { Box, Group, SimpleGrid, Skeleton, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import React, { Suspense } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import useCoinStore from '@/app/[locale]/price/[slug]/_store'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { useCoinStatistics } from '@/services/coin/market'
import {
  formatMarketCap,
  formatPercentage,
  formatPercentageColor,
  formatPercentageWithLimit,
} from '@/utils/number/format'

import InfoTooltip from './InfoTooltip'

function MarketDataFallback() {
  return (
    <Box
      mt={80}
      style={{
        borderRadius: 12,
        width: '100%',
      }}
    >
      <Skeleton
        h={32}
        w={160}
        radius={40}
        mb={12}
        style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
      />
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing={16}>
        {[0, 1, 2, 3, 4, 5, 6, 7].map(index => (
          <Box key={index}>
            <Skeleton
              h={16}
              w={120}
              mb={8}
              style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
            />
            <Skeleton
              h={20}
              w={80}
              style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
            />
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  )
}

function MarketDataContent() {
  const { coin } = useCoinContext()
  const { data: statisticsData } = useCoinStatistics(coin.slug)
  const marketDataT = useTranslations('marketData')
  const coinTableT = useTranslations('coinTable.headers')

  // Filter function to get the "all" period data
  const getPeriodData = (period: string) => {
    return statisticsData?.statistics?.find(item => item.period === period)
  }

  const allTimeData = getPeriodData('all')
  const symbol = coin?.symbol

  // Get all live data in one selector call
  const livePrice = useCoinStore(s => s.liveCoinMarket.usd_price)

  const coinPrice = livePrice ?? coin?.usd_price
  const coinMarketCap = coin?.usd_marketcap
  const coinVolume24h = coin?.usd_volume_24h

  // Get currency and formatted values
  const {
    currency,
    amounts: [{ formattedValue }],
  } = useCurrencyAmounts({
    amounts: [{ value: coinPrice }],
    tokenSymbol: coin?.symbol,
    isUsedForTokenPrice: true,
    isExchangeRateBasedOnSymbolCurrency: false,
  })

  const {
    amounts: [{ formattedValue: formattedMarketCap }, { formattedValue: formattedVolume24h }],
  } = useCurrencyAmounts({
    amounts: [{ value: coinMarketCap }, { value: coinVolume24h }],
    tokenSymbol: coin.symbol,
    valueFormatter: formatMarketCap,
    currencyFormatOptions: {},
  })

  if (!coin) return null

  // Remove commas from formatted value and convert to number
  const currentPrice = Number(formattedValue?.replace(/,/g, '')) || coinPrice || 1

  const allTimeHighChange = formatPercentageWithLimit(
    ((currentPrice - (allTimeData?.high || 0)) / currentPrice) * 100,
  )
  const allTimeLowChange = formatPercentageWithLimit(
    ((currentPrice - (allTimeData?.low || 0)) / currentPrice) * 100,
  )

  const allTimeHighChangeColor = formatPercentageColor(Number(allTimeHighChange.replace('%', '')))
  const allTimeLowChangeColor = formatPercentageColor(Number(allTimeLowChange.replace('%', '')))

  const contentItems = [
    {
      title: coinTableT('marketCap'),
      content: <Text variant="body1">{formattedMarketCap}</Text>,
      tooltip: marketDataT('marketCapTooltip'),
      value: coinMarketCap,
    },
    {
      title: marketDataT('items.rank'),
      content: <Text variant="body1">{`#${coin.rank}`}</Text>,
      tooltip: marketDataT('rankTooltip'),
      value: coin.rank,
    },
    {
      title: coinTableT('24hVolume'),
      content: <Text variant="body1">{formattedVolume24h}</Text>,
      tooltip: marketDataT('volume24hTooltip'),
      value: coinVolume24h,
    },
    {
      title: marketDataT('items.circulatingSupply'),
      content: (
        <Box>
          <Text variant="body1">{`${formatMarketCap(coin?.circulating_supply)} ${symbol}`}</Text>
          <Text variant="micro-text" mt={2} c="var(--text-secondary)">
            {`${Number(formatPercentage(coin?.circulating_supply / coin?.total_supply)) * 100}% ${marketDataT('ofTotal')}`}
          </Text>
        </Box>
      ),
      tooltip: marketDataT('circulatingSupplyTooltip'),
      value: coin?.circulating_supply,
    },
    {
      title: marketDataT('items.allTimeHigh'),
      content: (
        <Group gap={8}>
          <Text variant="body">{`${currency.symbol}${(allTimeData?.high || 0).toFixed(2)}`}</Text>
          <Text variant="body" c={allTimeHighChangeColor}>
            {allTimeHighChange}
          </Text>
        </Group>
      ),
      tooltip: marketDataT('allTimeHighTooltip'),
      value: allTimeData?.high,
    },
    {
      title: marketDataT('items.allTimeLow'),
      content: (
        <Group gap={8}>
          <Text variant="body">{`${currency.symbol}${(allTimeData?.low || 0).toFixed(2)}`}</Text>
          <Text variant="body" c={allTimeLowChangeColor}>
            {allTimeLowChange}
          </Text>
        </Group>
      ),
      tooltip: marketDataT('allTimeLowTooltip'),
      value: allTimeData?.low,
    },
    {
      title: marketDataT('items.totalSupply'),
      content: <Text variant="body1">{`${formatMarketCap(coin?.total_supply)} ${symbol}`}</Text>,
      tooltip: marketDataT('totalSupplyTooltip'),
      value: coin?.total_supply,
    },
    {
      title: marketDataT('items.maxSupply'),
      content: <Text variant="body1">{`${formatMarketCap(coin?.max_supply || 0)} ${symbol}`}</Text>,
      tooltip: marketDataT('marketCapTooltip'),
      value: coin?.max_supply,
    },
  ]

  // Filter out items with 0 values
  const filteredContentItems = contentItems.filter(item => {
    if (item.value === 0 || item.value === null || item.value === undefined) {
      return false
    }
    return true
  })

  return (
    <Box
      mt={80}
      style={{
        borderRadius: 12,
        width: '100%',
      }}
    >
      <Text variant="heading2" mb={12} component="h2">
        {marketDataT('title')}
      </Text>
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing={16}>
        {filteredContentItems.map((item, index) => (
          <InfoTooltip
            key={index}
            title={item.title}
            content={item.content}
            tooltip={item.tooltip}
          />
        ))}
      </SimpleGrid>
    </Box>
  )
}

function MarketData() {
  return (
    <Suspense fallback={<MarketDataFallback />}>
      <MarketDataContent />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(MarketData)
